import React, { useState, useEffect, useRef } from "react";
import { useKitchen } from "../contexts/KitchenContext";
import { formatError } from "../utils/api";
import {
  Upload, RefreshCw, Download, AlertTriangle, CheckCircle,
  TrendingUp, TrendingDown, Minus, Info, FileSpreadsheet,
  Calendar, BarChart3, Clock
} from "lucide-react";
import { exportCSV } from "../utils/csvUtils";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("manna_access_token")}` } });

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  match:          { label: "Match",        bg: "bg-[#EDF2EE]",  text: "text-[#637C64]",   icon: CheckCircle  },
  mismatch_low:   { label: "Minor Gap",    bg: "bg-amber-50",   text: "text-amber-700",   icon: TrendingDown },
  mismatch_high:  { label: "Major Gap",    bg: "bg-[#FBEAE7]",  text: "text-[#C2624E]",   icon: AlertTriangle },
  manna_only:     { label: "Not in Govt",  bg: "bg-purple-50",  text: "text-purple-700",  icon: TrendingUp   },
  govt_only:      { label: "Not in Manna", bg: "bg-blue-50",    text: "text-blue-700",    icon: Info         },
};

function DiffCell({ diff }) {
  if (diff === 0) return <span className="text-[#637C64] font-bold">✓</span>;
  const isPos = diff > 0;
  return (
    <span className={`font-bold text-sm ${isPos ? "text-[#637C64]" : "text-[#C2624E]"}`}>
      {isPos ? "+" : ""}{diff.toLocaleString()}
    </span>
  );
}

function StatCard({ label, value, sub, color = "text-[#3A322C]", bg = "bg-white" }) {
  return (
    <div className={`${bg} border border-[#E5E1DB] rounded-xl p-4`}>
      <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">{label}</p>
      <p className={`text-2xl font-extrabold font-[Manrope] ${color}`}>{value ?? "—"}</p>
      {sub && <p className="text-xs text-[#8A7E73] mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function Reconciliation() {
  const { kitchens, selectedKitchen } = useKitchen();
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [history, setHistory] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const fileRef = useRef();

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try {
      const r = await axios.get(`${API}/api/reconciliation/history`, auth());
      setHistory(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const fetchReport = async (d = date) => {
    setLoading(true); setError("");
    try {
      const params = { date: d };
      if (selectedKitchen) params.kitchen_id = selectedKitchen;
      const r = await axios.get(`${API}/api/reconciliation/report`, { ...auth(), params });
      setReport(r.data);
    } catch (e) { setError(formatError(e)); }
    setLoading(false);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true); setError(""); setSuccess("");
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const b64 = e.target.result; // already includes data:...;base64,
          const r = await axios.post(`${API}/api/reconciliation/upload`, {
            date,
            file_base64: b64,
            filename: file.name,
            kitchen_id: selectedKitchen || null,
          }, auth());
          setSuccess(`Uploaded: ${r.data.govt_records} schools parsed. ${r.data.matched} matched, ${r.data.total_diff_mdm} total MDM gap.`);
          await fetchReport(date);
          await fetchHistory();
        } catch (e2) { setError(formatError(e2)); }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (e) { setError(formatError(e)); setUploading(false); }
  };

  const handleExport = () => {
    if (!report) return;
    exportCSV(report.rows.map(r => ({
      school_name: r.school_name,
      govt_name: r.govt_name || "",
      mandal: r.mandal,
      status: r.status,
      manna_mdm: r.manna_mdm,
      govt_mdm: r.govt_mdm,
      diff_mdm: r.diff_mdm,
      manna_bf: r.manna_bf,
      govt_bf: r.govt_bf,
      diff_bf: r.diff_bf,
    })), `reconciliation_${date}`);
  };

  const filteredRows = (report?.rows || []).filter(r => {
    if (filterStatus && r.status !== filterStatus) return false;
    if (search && !r.school_name?.toLowerCase().includes(search.toLowerCase()) &&
        !r.mandal?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const s = report?.summary;

  return (
    <div className="space-y-5 max-w-7xl" data-testid="reconciliation">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">MDM Reconciliation</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">
            Compare Manna Trust's daily strength with the <strong>Telangana Govt MDM Portal</strong> — flag discrepancies before payment processing
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExport} disabled={!report}
            className="flex items-center gap-2 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] disabled:opacity-40">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* How it works banner */}
      <div className="bg-gradient-to-r from-[#5C5147]/5 to-[#E8956D]/5 border border-[#E8956D]/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet size={18} className="text-[#E8956D] shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-[#3A322C] text-sm mb-1">How to use this module</p>
            <ol className="text-xs text-[#5C5147] space-y-0.5">
              <li>1. Log into <span className="font-mono bg-[#F4F3F0] px-1 rounded">schooledu.telangana.gov.in/MDM/</span> with <strong>Mannatrust-admin / demo</strong></li>
              <li>2. Download today's XLS report from the portal</li>
              <li>3. Upload it here → the system auto-compares with Manna Trust's internal strength data</li>
              <li>4. Review discrepancies and raise with authorities where needed</li>
            </ol>
            <p className="text-xs text-[#8A7E73] mt-1.5">The portal requires manual login due to CAPTCHA. Once downloaded, everything else is automated.</p>
          </div>
        </div>
      </div>

      {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm">{error}</div>}
      {success && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-3 text-sm flex items-center gap-2"><CheckCircle size={14} />{success}</div>}

      {/* Date + Upload + Fetch row */}
      <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 flex flex-wrap gap-4 items-end">
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] font-medium" />
        </div>

        {/* Upload XLS */}
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">
            Upload Govt Portal XLS
          </label>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 bg-[#5C5147] text-white rounded-lg px-4 py-2 text-sm font-bold hover:bg-[#4A4038] disabled:opacity-60"
            data-testid="upload-xls-btn"
          >
            {uploading ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Parsing…" : "Upload XLS / XLSX"}
          </button>
          <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden"
            onChange={e => { if (e.target.files[0]) handleFileUpload(e.target.files[0]); e.target.value = ""; }} />
        </div>

        <button onClick={() => fetchReport()} disabled={loading}
          className="flex items-center gap-2 border border-[#E5E1DB] rounded-lg px-4 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] disabled:opacity-60">
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <BarChart3 size={14} />}
          View Report
        </button>

        {/* Upload history quick picks */}
        {history.length > 0 && (
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-xs text-[#8A7E73] font-bold">Recent:</span>
            {history.slice(0, 5).map(h => (
              <button key={h.date} onClick={() => { setDate(h.date); fetchReport(h.date); }}
                className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${h.date === date ? "border-[#E8956D] bg-[#E8956D]/10 text-[#E8956D]" : "border-[#E5E1DB] text-[#8A7E73] hover:border-[#E8956D]/50"}`}>
                <Clock size={9} className="inline mr-0.5" />
                {h.date}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {s && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <StatCard label="Matched Schools" value={s.matched} color="text-[#637C64]" bg="bg-[#EDF2EE]" />
            <StatCard label="Major Gaps" value={s.mismatches} color="text-[#C2624E]" bg="bg-[#FBEAE7]" />
            <StatCard label="Only in Manna" value={s.unmatched_manna} color="text-purple-700" bg="bg-purple-50" />
            <StatCard label="Only in Govt" value={s.unmatched_govt} color="text-blue-700" bg="bg-blue-50" />
            <div className="bg-white border border-[#E5E1DB] rounded-xl p-4 col-span-2">
              <p className="text-xs text-[#8A7E73] uppercase font-bold mb-2">MDM Totals</p>
              <div className="flex gap-4">
                <div><p className="text-xs text-[#8A7E73]">Manna</p><p className="text-lg font-extrabold text-green-700">{s.manna_total_mdm?.toLocaleString()}</p></div>
                <div><p className="text-xs text-[#8A7E73]">Govt</p><p className="text-lg font-extrabold text-blue-700">{s.govt_total_mdm?.toLocaleString()}</p></div>
                <div><p className="text-xs text-[#8A7E73]">Diff</p><p className={`text-lg font-extrabold ${s.total_diff_mdm > 0 ? "text-[#637C64]" : "text-[#C2624E]"}`}>{(s.manna_total_mdm - s.govt_total_mdm > 0 ? "+" : "")}{(s.manna_total_mdm - s.govt_total_mdm)?.toLocaleString()}</p></div>
              </div>
            </div>
          </div>

          {/* No govt data notice */}
          {!s.has_govt_data && (
            <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-5 py-4 flex items-center gap-3">
              <AlertTriangle size={18} className="text-[#D98032] shrink-0" />
              <div>
                <p className="font-bold text-[#3A322C] text-sm">No government data uploaded for {date}</p>
                <p className="text-xs text-[#8A7E73] mt-0.5">Upload the XLS from the Telangana MDM portal above to see the comparison.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter bar + Table */}
      {report && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            {/* Status filter buttons */}
            <button onClick={() => setFilterStatus("")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-colors ${!filterStatus ? "border-[#E8956D] bg-[#E8956D]/10 text-[#E8956D]" : "border-[#E5E1DB] text-[#5C5147]"}`}>
              All ({report.rows.length})
            </button>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
              const count = report.rows.filter(r => r.status === key).length;
              if (count === 0) return null;
              return (
                <button key={key} onClick={() => setFilterStatus(filterStatus === key ? "" : key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-colors ${filterStatus === key ? `border-current ${cfg.bg} ${cfg.text}` : "border-[#E5E1DB] text-[#5C5147]"}`}>
                  <cfg.icon size={11} />
                  {cfg.label} ({count})
                </button>
              );
            })}
            <div className="ml-auto relative">
              <input placeholder="Search school…" value={search} onChange={e => setSearch(e.target.value)}
                className="border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-xs outline-none focus:border-[#E8956D] w-48" />
            </div>
          </div>

          <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[#F4F3F0] flex items-center justify-between">
              <p className="text-sm font-bold text-[#3A322C]">{filteredRows.length} schools shown</p>
              {loading && <RefreshCw size={14} className="animate-spin text-[#E8956D]" />}
            </div>
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F3F0] sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">School Name</th>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">Mandal</th>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">Status</th>
                    <th className="text-center px-3 py-3 text-xs font-bold uppercase text-green-700 whitespace-nowrap" colSpan={3}>
                      ← MDM (Lunch) →
                    </th>
                    <th className="text-center px-3 py-3 text-xs font-bold uppercase text-amber-700 whitespace-nowrap" colSpan={3}>
                      ← Breakfast →
                    </th>
                  </tr>
                  <tr className="bg-[#F8F8F6]">
                    <th colSpan={3} />
                    <th className="text-center px-3 py-2 text-[10px] font-bold text-green-600">Manna</th>
                    <th className="text-center px-3 py-2 text-[10px] font-bold text-blue-600">Govt</th>
                    <th className="text-center px-3 py-2 text-[10px] font-bold text-[#5C5147]">Diff</th>
                    <th className="text-center px-3 py-2 text-[10px] font-bold text-amber-600">Manna</th>
                    <th className="text-center px-3 py-2 text-[10px] font-bold text-blue-600">Govt</th>
                    <th className="text-center px-3 py-2 text-[10px] font-bold text-[#5C5147]">Diff</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F4F3F0]">
                  {filteredRows.map((row, i) => {
                    const cfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.match;
                    return (
                      <tr key={`idx-${i}`} className={`hover:bg-[#FAFAF8] ${row.status.includes("mismatch") ? "border-l-4 border-l-[#C2624E]/30" : ""}`}>
                        <td className="px-4 py-2.5">
                          <p className="text-sm font-medium text-[#3A322C] leading-tight">{row.school_name}</p>
                          {row.govt_name && row.govt_name !== row.school_name && (
                            <p className="text-[10px] text-[#8A7E73] mt-0.5">Govt: {row.govt_name}</p>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-[#8A7E73]">{row.mandal || "—"}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 w-fit ${cfg.bg} ${cfg.text}`}>
                            <cfg.icon size={9} /> {cfg.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center font-bold text-green-700">{row.manna_mdm > 0 ? row.manna_mdm.toLocaleString() : <span className="text-[#C4BAAF]">—</span>}</td>
                        <td className="px-3 py-2.5 text-center font-bold text-blue-700">{row.govt_mdm > 0 ? row.govt_mdm.toLocaleString() : <span className="text-[#C4BAAF]">—</span>}</td>
                        <td className="px-3 py-2.5 text-center"><DiffCell diff={row.diff_mdm} /></td>
                        <td className="px-3 py-2.5 text-center font-bold text-amber-700">{row.manna_bf > 0 ? row.manna_bf.toLocaleString() : <span className="text-[#C4BAAF]">—</span>}</td>
                        <td className="px-3 py-2.5 text-center font-bold text-blue-700">{row.govt_bf > 0 ? row.govt_bf.toLocaleString() : <span className="text-[#C4BAAF]">—</span>}</td>
                        <td className="px-3 py-2.5 text-center"><DiffCell diff={row.diff_bf} /></td>
                      </tr>
                    );
                  })}
                  {filteredRows.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-10 text-[#8A7E73]">No records match the current filter</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!report && !loading && (
        <div className="text-center py-20 bg-white border border-[#E5E1DB] rounded-2xl">
          <FileSpreadsheet size={48} className="mx-auto mb-4 text-[#C4BAAF]" />
          <h3 className="font-bold text-[#3A322C] mb-2">Upload today's report to begin</h3>
          <p className="text-sm text-[#8A7E73] max-w-sm mx-auto">Download the XLS from the Telangana MDM portal and upload it above. The system will automatically compare it with Manna Trust's internal strength data.</p>
        </div>
      )}

      {loading && !report && (
        <div className="text-center py-16"><RefreshCw size={24} className="animate-spin text-[#E8956D] mx-auto" /></div>
      )}
    </div>
  );
}
